import { Checkbox, cn } from "@nextui-org/react";
import {
  SortDescriptor
} from "@nextui-org/table";
import { TableVirtuoso } from 'react-virtuoso';
/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unnecessary-type-assertion */
import { RiskAssessmentTypes } from "@/modules/agent/types";
import clsx from "clsx";
import isEqual from "lodash/isEqual";
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from "react";
import ErrorComponent from "../ErrorComponent/ErrorComponent";
import ErrorBoundary from "../error-boundary";
import { ZTableColumnProps, ZTableProps, ZTableRowId, ZTableRowSelectionModel, ZTableValidRowModel } from "./index";

const VirtualizedTable = <T extends ZTableValidRowModel = ZTableValidRowModel>({
  rows,
  columns,
  getRowId,
  loading,
  sortDescriptor: defaultSortModel,
  onSortModelChange,
  isRowSelectable,
  isRowDisabled,
  isRowActive,
  rowSelectionModel: defaultRowSelectionModel,
  onRowSelectionModelChange,
  tableHeaderProps,
  tableColumnProps,
  tableBodyProps,
  tableRowProps,
  tableCellProps,
  tableRowSelectionCheckboxProps,
  showCheckboxIfDisabled = true,
  visibleColumns,
  isReportReady,
  onRowClick,
  agentSubType,
  showCheckboxes = true,
  isSortingControlled = false,
  ...zTableProps
}: ZTableProps<T>) => {
  const [sortDescriptor, setSortModel] = useState<SortDescriptor | undefined>(
    defaultSortModel
  );
  const [rowSelectionModel, setRowSelectionModel] = useState<
    ZTableRowSelectionModel | undefined
  >(defaultRowSelectionModel);

  const selectableRows = useMemo(() => {
    const filteredRows = rows.filter((row) => {
      const disabled = isRowDisabled?.({ row });
      const selectable = isRowSelectable?.({ row });
      return !(disabled || selectable === false);
    });
    return filteredRows;
  }, [rows, isRowDisabled, isRowSelectable]);

  const shouldRenderSelectionColumn = useMemo(() => {
    return (
      !isReportReady && agentSubType !== RiskAssessmentTypes.FAIR &&
      showCheckboxes &&
      (typeof isRowSelectable === "function" ||
        Array.isArray(rowSelectionModel) ||
        typeof onRowSelectionModelChange === "function")
    );
  }, [
    rows,
    isRowSelectable,
    rowSelectionModel,
    onRowSelectionModelChange,
    isReportReady,
    showCheckboxes,
  ]);

  useLayoutEffect(() => {
    document.querySelector("[data-row-active='true']")?.scrollIntoView({
      block: "center",
    });
  }, []);

  useEffect(() => {
    if (defaultSortModel) {
      setSortModel(defaultSortModel);
    }
  }, [defaultSortModel, setSortModel]);

  useEffect(() => {
    if (defaultRowSelectionModel) {
      setRowSelectionModel(defaultRowSelectionModel);
    }
  }, [defaultRowSelectionModel, setRowSelectionModel]);

  const sortedRows = useMemo(() => {
    if (!sortDescriptor?.column || !sortDescriptor?.direction || isSortingControlled) {
      return rows;
    }
    const column = columns?.find(
      (column) => column.fieldName === sortDescriptor?.column
    );
    if (!column) {
      return rows;
    }
    const sortComparator =
      typeof column.sortComparator === "function"
        ? column.sortComparator
        : (a: T, b: T) => {
          // extracts the sort value from the row
          const extractSortValue = (x: any) => {
            if (typeof column.getSortValue === "function") {
              return column.getSortValue(x);
            } else if (sortDescriptor?.column) {
              return x[sortDescriptor?.column];
            } else {
              return undefined;
            }
          };

          // returns true if value is undefined or null
          const nullUndefinedCheck = (value: any) => {
            if (value === undefined || value === null) return true;
            return false;
          };

          // makes sure all values are of the same type
          const allTypesSame = (
            values: any[],
            type?: string,
            instanceOfType?: any
          ) => {
            if (
              instanceOfType &&
              values.every((value) => value instanceof instanceOfType)
            ) {
              return true;
            } else if (
              type &&
              values.every((value) => typeof value === type)
            ) {
              return true;
            } else {
              return false;
            }
          };

          if (nullUndefinedCheck(a) && nullUndefinedCheck(b)) {
            return 0;
          }

          const aValue = extractSortValue(a);
          const bValue = extractSortValue(b);

          if (nullUndefinedCheck(aValue) && nullUndefinedCheck(bValue)) {
            return 0;
          }
          if (nullUndefinedCheck(aValue)) {
            return 1;
          }
          if (nullUndefinedCheck(bValue)) {
            return -1;
          }

          if (allTypesSame([aValue, bValue], "string")) {
            return aValue.localeCompare(bValue);
          }
          if (allTypesSame([aValue, bValue], "number")) {
            return aValue - bValue;
          }
          if (allTypesSame([aValue, bValue], undefined, Date)) {
            return aValue.getTime() - bValue.getTime();
          }

          return String(aValue).localeCompare(String(bValue));
        };
    const sortedRows = [...rows].sort(sortComparator);
    return sortDescriptor?.direction === "descending"
      ? sortedRows.reverse()
      : sortedRows;
  }, [rows, columns, sortDescriptor, isSortingControlled]);

  const handleSortClick = useCallback(
    (columnSortModel: SortDescriptor) => {
      const newSortDescriptor: SortDescriptor = {
        column: columnSortModel.column,
        direction:
          sortDescriptor?.column === columnSortModel.column
            ? sortDescriptor?.direction === "ascending"
              ? "descending"
              : "ascending"
            : "ascending",
      };
      setSortModel(newSortDescriptor);
      onSortModelChange?.(newSortDescriptor);
    },
    [sortDescriptor, onSortModelChange]
  );

  const handleSelectAllRowClick = useCallback(() => {
    const newSelection =
      selectableRows?.length === rowSelectionModel?.length
        ? []
        : selectableRows?.map((row) => getRowId?.(row) ?? row?.id) ?? [];
    onRowSelectionModelChange?.(newSelection);
  }, [selectableRows, rowSelectionModel, getRowId, onRowSelectionModelChange]);

  const handleSelectRowClick = useCallback(
    (rowId: ZTableRowId) => {
      setRowSelectionModel((prev) => {
        return isEqual(prev, rowSelectionModel) ? prev : prev?.includes(rowId)
          ? prev.filter((id) => id !== rowId)
          : [...(prev ?? []), rowId];
      });
      onRowSelectionModelChange?.(
        rowSelectionModel?.includes(rowId)
          ? rowSelectionModel.filter((id) => id !== rowId)
          : [...(rowSelectionModel ?? []), rowId]
      );
    },
    [rowSelectionModel, onRowSelectionModelChange]
  );

  const handleOnRowClick = useCallback(
    (
      e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
      row: T,
      rowIndex: number
    ) => {
      const selection = window.getSelection();
      if (selection && selection.type === "Range") {
        return;
      }

      const rowId = getRowId?.(row) ?? row?.id;

      if (rowSelectionModel && rowSelectionModel.length > 0) {
        e.preventDefault(); // Prevent default row click behavior
        handleSelectRowClick(rowId);
      } else if (onRowClick) {
        e.preventDefault();
        onRowClick({ row });
      } else {
        if (!tableRowProps) return;
        if (typeof tableRowProps === "function")
          tableRowProps(row, rowIndex).onClick?.(e);
        else tableRowProps?.onClick?.(e);
      }
    },
    [
      rowSelectionModel,
      getRowId,
      handleSelectRowClick,
      tableRowProps,
      onRowClick,
    ]
  );
  const tableColumnsProps = [
    ...(shouldRenderSelectionColumn
      ? [
        {
          fieldName: "selection",
          headerClassName: 'w-[83px]',
          renderHeader: () => {
            return (
              <div className="w-[75px] flex justify-center items-center pr-5">
                <Checkbox
                  disabled={loading}
                  isDisabled={loading || selectableRows?.length === 0}
                  isSelected={
                    rowSelectionModel?.length === selectableRows?.length
                  }
                  isIndeterminate={
                    rowSelectionModel &&
                    rowSelectionModel?.length > 0 &&
                    rowSelectionModel?.length < selectableRows?.length
                  }
                  onChange={handleSelectAllRowClick}
                  data-component="z-table-header-select"
                  classNames={{
                    icon: "m-0",
                    wrapper: "m-0",
                    base: "m-0",
                    label: "m-0",
                  }}
                  className="after:bg-foreground after:text-background text-background m-0"
                />
              </div>
            );
          },
        } as ZTableColumnProps<T>,
      ]
      : []),
    ...(visibleColumns || columns),
  ].map((col, columnIndex) => ({ ...col, columnIndex }));

  const tableRowsProps = sortedRows.map((item, idx) => ({ ...item, idx }));

  // Extract width from headerClassName if present
  const extractWidthFromClassName = useCallback((className?: string) => {
    if (!className) return null;

    // Check for specific pixel widths: w-[100px], w-[240px], min-w-[250px], etc.
    const pixelWidthMatch = className.match(/(?:^|\s)(?:w-\[(\d+)px\]|min-w-\[(\d+)px\]|max-w-\[(\d+)px\])/);
    if (pixelWidthMatch) {
      const width = pixelWidthMatch[1] || pixelWidthMatch[2] || pixelWidthMatch[3];
      return width ? parseInt(width) : null;
    }

    // Check for Tailwind width classes: w-1, w-2, w-3, ..., w-96
    const tailwindWidthMatch = className.match(/(?:^|\s)w-(\d+)(?:\s|$)/);
    if (tailwindWidthMatch) {
      const widthValue = parseInt(tailwindWidthMatch[1]);
      // Convert Tailwind width units to pixels (each unit is 0.25rem, and 1rem = 16px)
      return widthValue * 4; // 4px per unit
    }

    // Check for percentage widths: w-1/2, w-1/3, w-1/4, w-2/3, etc.
    if (className.includes('w-1/2')) return '50%';
    if (className.includes('w-1/3')) return '33.333333%';
    if (className.includes('w-2/3')) return '66.666667%';
    if (className.includes('w-1/4')) return '25%';
    if (className.includes('w-3/4')) return '75%';
    if (className.includes('w-1/5')) return '20%';
    if (className.includes('w-2/5')) return '40%';
    if (className.includes('w-3/5')) return '60%';
    if (className.includes('w-4/5')) return '80%';

    // Check for special width classes that should preserve original behavior
    if (className.includes('w-full')) return '100%';
    if (className.includes('flex-1')) return 'flex-1';
    if (className.includes('w-auto')) return 'auto';

    return null;
  }, []);

  // Calculate column widths based on content
  const calculateColumnWidths = useCallback(() => {
    const widths: { [key: string]: string | number } = {};

    // Set minimum width for selection column
    if (shouldRenderSelectionColumn) {
      widths['selection'] = 80;
    }

    // Calculate widths for other columns based on content or headerClassName
    tableColumnsProps.forEach((column) => {
      if (column.fieldName === 'selection') return;

      // First check if width is specified in headerClassName
      const classNameWidth = extractWidthFromClassName(column.headerClassName);
      if (classNameWidth !== null) {
        if (classNameWidth === 'flex-1') {
          // For flex-1, don't set a specific width, let CSS handle it
          return;
        }
        widths[column.fieldName] = classNameWidth;
        return;
      }

      // Check for special width classes that should preserve original behavior
      if (column.headerClassName?.includes('w-full') ||
        column.headerClassName?.includes('w-auto') ||
        column.headerClassName?.includes('min-w-') ||
        column.headerClassName?.includes('max-w-')) {
        // Don't override these, let CSS handle them
        return;
      }

      // Get header width for content-based calculation
      const headerText = typeof column?.renderHeader === "function"
        ? String(column?.renderHeader(column))
        : column?.headerName ?? "";

      let maxWidth = headerText.length * 8 + 40; // Base width for header

      // Sample some rows to calculate content width
      const sampleSize = Math.min(rows.length, 10);
      for (let i = 0; i < sampleSize; i++) {
        const row = rows[i];
        let cellContent = '';

        if (typeof column.renderCell === "function") {
          const rendered = column.renderCell(row);
          cellContent = String(rendered).replace(/<[^>]*>/g, ''); // Remove HTML tags
        } else if (typeof column.fieldName === "string") {
          cellContent = String(row[column.fieldName] || '');
        }

        const contentWidth = cellContent.length * 8 + 24; // Approximate width
        maxWidth = Math.max(maxWidth, contentWidth);
      }

      // Use minWidth from column definition if available, otherwise set min and max bounds
      const minWidth = typeof column.minWidth === 'number' ? column.minWidth : 100;
      const finalWidth = Math.max(minWidth, Math.min(maxWidth, 400));
      widths[column.fieldName] = finalWidth;
    });

    return widths;
  }, [tableColumnsProps, rows, shouldRenderSelectionColumn, extractWidthFromClassName]);

  const columnWidths = useMemo(() => calculateColumnWidths(), [calculateColumnWidths]);


  // Virtualized table components
  const TableComponents: any = useMemo(() => ({
    Table: ({ style, ...props }: any) => (
      <table
        {...props}
        style={{ ...style, width: '100%', tableLayout: 'fixed' }}
        className={cn(
          "min-w-full caption-bottom text-sm !table-fixed",
          "p-0 [&>tbody>tr:hover]:bg-default-100/50",
          "[&>tbody>tr]:border-b-1 [&>tbody>tr]:border-divider",
          "[&>tbody>tr:last-child]:border-b-0",
          props.className
        )}
        data-component="z-table"
      />
    ),
    TableHead: forwardRef<HTMLTableSectionElement, any>(({ ...props }, ref) => (
      <thead
        ref={ref}
        {...props}
        className="[&>tr]:border-b-1 [&>tr]:border-divider bg-default-100 rounded-t-lg sticky top-0 z-20"
        style={{ position: 'sticky', top: 0, zIndex: 20 }}
      />
    )),
    TableBody: forwardRef<HTMLTableSectionElement, any>(({ ...props }, ref) => (
      <tbody ref={ref} {...props} className="h-screen divide-y divide-divider" />
    )),
    TableRow: forwardRef<HTMLDivElement, any>(({ ...props }, ref) => {
      return (
        <tr
          {...props}
          ref={ref as any}
          className={cn(
            "group outline-none",
            "data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2",
            "data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2",
            "cursor-default",
            "data-[selectable=true]:cursor-pointer",
            "data-[hover=true]:bg-default-100",
            "data-[selected=true]:bg-default-100",
            "data-[disabled=true]:opacity-disabled",
            props.className
          )}
          data-component="z-table-row"
        />
      );
    }),
  }), []);

  const VirtualTableRow = useCallback((index: number) => {
    const row = tableRowsProps[index];
    if (!row) return null;

    const disabled = isRowDisabled?.({ row });
    const selectable = isRowSelectable?.({ row });
    const active = isRowActive?.({ row });

    return (
      <>
        {tableColumnsProps.map((column, columnIndex) => {
          const width = columnWidths[column.fieldName];
          const shouldApplyInlineWidth = typeof width === 'number';

          if (column.fieldName === "selection") {
            const rowId = getRowId?.(row) ?? row?.id;

            return (
              <td
                key={columnIndex}
                style={shouldApplyInlineWidth ? {
                  width: `${width}px`,
                  minWidth: `${width}px`,
                  maxWidth: `${width}px`
                } : {}}
                className={cn(
                  "w-[72px] ml-0 py-6 px-5 flex justify-center items-center",
                  "group-hover:[&>.table-checkbox]:visible [&>.table-checkbox]:invisible",
                  "[&>.visible]:!visible"
                )}
              >
                <Checkbox
                  disabled={disabled}
                  isDisabled={disabled}
                  isSelected={
                    ((showCheckboxIfDisabled && disabled) ||
                      rowSelectionModel?.includes?.(row.id)) ??
                    false
                  }
                  classNames={{
                    base: cn(
                      "group",
                      disabled && "opacity-50",
                      "m-0"
                    ),
                    wrapper: cn(
                      "group-data-[disabled=true]:border-default",
                      "after:group-data-[disabled=true]:bg-default-100",
                      "m-0"
                    ),
                    icon: "group-data-[disabled=true] m-0",
                  }}
                  className={cn(
                    "table-checkbox",
                    ((showCheckboxIfDisabled && disabled) ||
                      rowSelectionModel?.includes?.(row.id)) ??
                      false
                      ? "visible"
                      : ""
                  )}
                  onChange={() => {
                    if (!disabled) {
                      handleSelectRowClick(rowId);
                    }
                  }}
                  data-component="z-table-row-select"
                  color={disabled ? "default" : "primary"}
                  {...(typeof tableRowSelectionCheckboxProps === "function"
                    ? tableRowSelectionCheckboxProps({ row, rowIndex: row.idx })
                    : tableRowSelectionCheckboxProps)}
                />
              </td>
            );
          }

          return (
            <td
              key={columnIndex}
              style={{
                ...(shouldApplyInlineWidth ? {
                  width: `${width}px`,
                  minWidth: `${width}px`,
                  maxWidth: `${width}px`
                } : {}),
                // Force width from headerClassName if present
                ...(column.headerClassName?.includes('w-[') ? {
                  width: column.headerClassName.match(/w-\[(\d+)px\]/)?.[1] ? `${column.headerClassName.match(/w-\[(\d+)px\]/)?.[1]}px` : undefined,
                  minWidth: column.headerClassName.match(/w-\[(\d+)px\]/)?.[1] ? `${column.headerClassName.match(/w-\[(\d+)px\]/)?.[1]}px` : undefined,
                  maxWidth: column.headerClassName.match(/w-\[(\d+)px\]/)?.[1] ? `${column.headerClassName.match(/w-\[(\d+)px\]/)?.[1]}px` : undefined,
                } : {})
              }}
              className={cn(
                "px-3 py-4 text-sm align-middle",
                "relative overflow-hidden", // Add overflow hidden to prevent text overflow
                clsx(
                  column.headerClassName,
                  column.headerClassName?.includes("sticky-right") ? "sticky-right" : "",
                  typeof tableCellProps === "function"
                    ? tableCellProps({
                      row,
                      rowIndex: row.idx,
                      column,
                      columnIndex,
                    })?.className || ""
                    : tableCellProps?.className || ""
                )
              )}
              data-component="z-table-cell"
            >
              <div className="truncate"> {/* Wrap content in truncate div */}
                {typeof column.renderCell === "function"
                  ? column.renderCell(row)
                  : typeof column.fieldName === "string"
                    ? row[column.fieldName]
                    : ""}
              </div>
            </td>
          );
        })}
      </>
    );
  }, [
    tableRowsProps,
    tableColumnsProps,
    isRowDisabled,
    isRowSelectable,
    isRowActive,
    rowSelectionModel,
    getRowId,
    handleOnRowClick,
    showCheckboxIfDisabled,
    handleSelectRowClick,
    tableRowSelectionCheckboxProps,
    tableCellProps,
    columnWidths
  ]);

  if (!columns || columns.length === 0) {
    return (
      <></>
    );
  }
  const fixedHeaderContent = useCallback(() => (
    <tr className="group outline-none data-[focus-visible=true]:z-20 data-[focus-visible=true]:outline-2 data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2">
      {tableColumnsProps.map((column, index) => {
        const renderHeader = () => {
          return typeof column?.renderHeader === "function"
            ? column?.renderHeader(column)
            : column?.headerName ?? "";
        };

        const width = columnWidths[column.fieldName];
        const shouldApplyInlineWidth = typeof width === 'number';

        return (
          <th
            key={column.fieldName || `column-${index}`}
            style={{
              ...(shouldApplyInlineWidth ? {
                width: `${width}px`,
                minWidth: `${width}px`,
                maxWidth: `${width}px`
              } : {}),
              // Force width from headerClassName if present
              ...(column.headerClassName?.includes('w-[') ? {
                width: column.headerClassName.match(/w-\[(\d+)px\]/)?.[1] ? `${column.headerClassName.match(/w-\[(\d+)px\]/)?.[1]}px` : undefined,
                minWidth: column.headerClassName.match(/w-\[(\d+)px\]/)?.[1] ? `${column.headerClassName.match(/w-\[(\d+)px\]/)?.[1]}px` : undefined,
                maxWidth: column.headerClassName.match(/w-\[(\d+)px\]/)?.[1] ? `${column.headerClassName.match(/w-\[(\d+)px\]/)?.[1]}px` : undefined,
              } : {})
            }}
            className={cn(
              "w-auto group h-10 px-3 text-left align-middle font-normal text-foreground-500 outline-none",
              "data-[sortable=true]:transition-colors data-[hover=true]:text-foreground-400",
              "data-[sortable=true]:cursor-pointer",
              "first:rounded-l-lg last:rounded-r-lg",
              "text-tiny font-medium text-default-600 cursor-default overflow-hidden", // Add overflow hidden
              clsx("cursor-default", column.headerClassName),
              column?.sortable && "cursor-pointer hover:text-foreground-400"
            )}
            data-sortable={column?.sortable}
            data-hover={column?.sortable}
            onClick={() => {
              if (column?.sortable) {
                handleSortClick({ column: column.fieldName, direction: 'ascending' });
              }
            }}
          >
            <div className="flex items-center gap-2">
              <span className="text-tiny font-semibold text-default-700">
                {renderHeader()}
              </span>
              {column?.sortable && sortDescriptor?.column === column.fieldName && (
                <span className="text-xs text-default-500">
                  {sortDescriptor.direction === 'ascending' ? '↑' : '↓'}
                </span>
              )}
            </div>
          </th>
        );
      })}
    </tr>
  ), [tableColumnsProps, sortDescriptor, handleSortClick, columnWidths]);

  const getItemProps = useCallback((index: number) => {
    const row = tableRowsProps[index];
    if (!row) return {};

    const disabled = isRowDisabled?.({ row });
    const selectable = isRowSelectable?.({ row });
    const active = isRowActive?.({ row });
    const isSelected = rowSelectionModel?.includes?.(getRowId?.(row) ?? row?.id);

    return {
      className: cn(
        "group outline-none",
        "data-[focus-visible=true]:z-10 data-[focus-visible=true]:outline-2",
        "data-[focus-visible=true]:outline-focus data-[focus-visible=true]:outline-offset-2",
        "cursor-default",
        "data-[selectable=true]:cursor-pointer",
        "data-[hover=true]:bg-default-100",
        "data-[selected=true]:bg-default-100",
        active && "bg-primary-50",
        isSelected && "bg-default-100",
        disabled && "data-[disabled=true]:opacity-disabled"
      ),
      'data-row-selected': isSelected,
      'data-row-disabled': disabled,
      'data-row-selectable': selectable !== false && !disabled,
      'data-row-active': active,
      'data-selectable': selectable !== false && !disabled,
      'data-selected': isSelected,
      'data-disabled': disabled,
      'data-hover': !disabled,
      onClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
        e.stopPropagation();
        handleOnRowClick(e, row, row.idx);
      }
    };
  }, [tableRowsProps, isRowDisabled, isRowSelectable, isRowActive, rowSelectionModel, getRowId, handleOnRowClick]);

  return (
    <ErrorBoundary fallback={<ErrorComponent />}>
      <div
        className={cn("relative w-full", zTableProps.className)}
        data-component="z-table"
        style={{
          height: zTableProps.style?.height || "100%",
          ...zTableProps.style
        }}
      >
        <TableVirtuoso
          style={{ height: "100%", width: '100%' }}
          data={tableRowsProps}
          components={TableComponents}
          fixedHeaderContent={fixedHeaderContent}
          itemContent={VirtualTableRow}
          computeItemKey={(index) => tableRowsProps[index]?.id || index}
          overscan={20}
        />
      </div>
    </ErrorBoundary>
  );
};

export { VirtualizedTable };

